import { Link, useRouterState } from "@tanstack/react-router";
import {
  Inbox,
  FlaskConical,
  Brain,
  BarChart3,
  Bell,
  FileText,
  Users,
  Activity,
  Power,
  Settings,
  ChevronUp,
  ChevronLeft,
  LogOut,
  Home,
  Calendar,
  MessageSquare,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useConversations } from "@/hooks/use-conversations";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { StatusDot, type StatusDotVariant } from "@/components/layout/status-dot";
import { CLIENTS, findClient, type ClientCard } from "@/lib/clients";

interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
  count?: number;
  adminOnly?: boolean;
  /** Si true, se renderiza atenuado y sin navegación (sección pendiente). */
  disabled?: boolean;
}

/**
 * Sidebar v2.2 — context-aware.
 *
 * Detecta si pathname empieza con /c/:slug. Si sí, modo workspace:
 *   - Wordmark "cm.champe" arriba.
 *   - Breadcrumb "← Todos los clientes".
 *   - Header del cliente activo (status + nombre).
 *   - Nav scoped: Inbox / Publicaciones / Métricas / Alertas / Cuentas / Skills.
 *   - Separador.
 *   - Global tools: Sandbox / Audit log / Admin.
 *
 * Si no, modo home/global:
 *   - Wordmark.
 *   - Link "Inicio".
 *   - Sección Clientes — lista click → /c/:slug.
 *   - Sección Global — Sandbox / Audit log.
 *   - Sección Admin role-gated.
 */
export function Sidebar() {
  const { data: user } = useCurrentUser();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const activeClientMatch = pathname.match(/^\/c\/([^/]+)/);
  const activeClient: ClientCard | undefined = activeClientMatch
    ? findClient(activeClientMatch[1])
    : undefined;

  return (
    <nav
      aria-label="Navegación principal"
      className={cn(
        "flex flex-col shrink-0",
        "bg-surface-1 border-r border-border-subtle",
        "w-[60px] lg:w-sidebar-wide",
      )}
    >
      {/* Wordmark ───────────────────────────────────────────────────── */}
      <div className="h-16 flex items-center gap-2 px-4 border-b border-border-subtle">
        <Link
          to="/"
          className="wordmark-sm text-text-primary hover:opacity-80 transition-opacity flex items-baseline"
          aria-label="cm.champe — ir al inicio"
        >
          <span className="hidden lg:inline">cm</span>
          <span className="text-magenta">.</span>
          <span className="hidden lg:inline">champe</span>
          <span className="lg:hidden">cm</span>
        </Link>
      </div>

      {/* Scrollable nav ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto py-3">
        {activeClient ? (
          <WorkspaceNav
            client={activeClient}
            pathname={pathname}
            isAdmin={user?.role === "admin"}
          />
        ) : (
          <GlobalNav pathname={pathname} isAdmin={user?.role === "admin"} />
        )}
      </div>

      {/* User menu ─────────────────────────────────────────────────── */}
      {user && <UserMenu name={user.name} role={user.role} />}
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Workspace nav (cuando estás dentro de un cliente)
// ─────────────────────────────────────────────────────────────────────

function WorkspaceNav({
  client,
  pathname,
  isAdmin,
}: {
  client: ClientCard;
  pathname: string;
  isAdmin: boolean;
}) {
  const { data: convs } = useConversations({ client: client.slug });

  const inboxCount = (convs ?? []).filter(
    (c) => c.state === "bot" || c.state === "human" || c.state === "escalated",
  ).length;
  const overdueCount = (convs ?? []).filter((c) => c.slaRemainingSec < 0).length;

  const statusVariant: StatusDotVariant =
    client.health === "ok"
      ? "success"
      : client.health === "warn"
        ? "warning"
        : "muted";

  const base = `/c/${client.slug}`;
  const workspace: NavItem[] = [
    {
      path: `${base}/inbox`,
      icon: Inbox,
      label: "Inbox",
      count: inboxCount || undefined,
    },
    { path: `${base}/monitoreo`, icon: Activity, label: "Monitoreo" },
    { path: `${base}/publicaciones`, icon: Calendar, label: "Publicaciones", disabled: true },
    { path: `${base}/metricas`, icon: BarChart3, label: "Métricas", disabled: true },
    {
      path: `${base}/alertas`,
      icon: Bell,
      label: "Alertas",
      count: overdueCount || undefined,
    },
    { path: `${base}/cuentas`, icon: Users, label: "Cuentas" },
    { path: `${base}/skills`, icon: Brain, label: "Manuales" },
    { path: `${base}/simulador`, icon: MessageSquare, label: "Simulador" },
  ];

  const global: NavItem[] = [
    { path: "/sandbox", icon: FlaskConical, label: "Sandbox" },
    { path: "/audit", icon: FileText, label: "Audit log" },
  ];

  const admin: NavItem[] = [
    { path: "/admin", icon: Power, label: "Kill switch" },
  ];

  return (
    <>
      {/* Breadcrumb */}
      <div className="px-3 mb-2 hidden lg:block">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-2xs text-text-muted hover:text-text-primary font-sans"
        >
          <ChevronLeft size={12} strokeWidth={1.75} />
          Todos los clientes
        </Link>
      </div>

      {/* Header del cliente */}
      <Link
        to="/c/$clientSlug"
        params={{ clientSlug: client.slug }}
        className={cn(
          "mx-2 mb-3 rounded-lg px-3 py-2 flex items-center gap-2",
          "hover:bg-surface-2 transition-colors",
          pathname === `/c/${client.slug}` && "bg-surface-3",
        )}
      >
        <StatusDot variant={statusVariant} size={8} className="shrink-0" />
        <div className="hidden lg:flex flex-col min-w-0 flex-1">
          <span className="font-sans text-sm font-medium text-text-primary truncate">
            {client.label}
          </span>
          <span className="font-sans text-2xs text-text-muted truncate">
            {client.market}
          </span>
        </div>
      </Link>

      <NavSection label="Workspace">
        {workspace.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            active={isActive(pathname, item.path)}
          />
        ))}
      </NavSection>

      <NavSection label="Global">
        {global.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            active={isActive(pathname, item.path)}
          />
        ))}
      </NavSection>

      {isAdmin && (
        <NavSection label="Admin">
          {admin.map((item) => (
            <NavLink
              key={item.path}
              item={item}
              active={isActive(pathname, item.path)}
            />
          ))}
        </NavSection>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Global nav (home + cross-cliente)
// ─────────────────────────────────────────────────────────────────────

function GlobalNav({
  pathname,
  isAdmin,
}: {
  pathname: string;
  isAdmin: boolean;
}) {
  const { data: user } = useCurrentUser();
  const accessibleClients = CLIENTS.filter((c) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return user.clientAccess.includes(c.slug);
  });

  const home: NavItem[] = [{ path: "/", icon: Home, label: "Inicio" }];
  const tools: NavItem[] = [
    { path: "/sandbox", icon: FlaskConical, label: "Sandbox" },
    { path: "/audit", icon: FileText, label: "Audit log" },
  ];
  const admin: NavItem[] = [
    { path: "/admin", icon: Power, label: "Kill switch" },
    { path: "/admin/users", icon: Settings, label: "Usuarios" },
  ];

  return (
    <>
      <NavSection label="Workspace">
        {home.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            active={pathname === "/"}
          />
        ))}
      </NavSection>

      <NavSection label="Clientes">
        {accessibleClients.map((client) => (
          <ClientLink key={client.slug} client={client} pathname={pathname} />
        ))}
        {accessibleClients.length === 0 && (
          <li className="px-3 py-1.5 text-2xs text-text-muted font-sans">
            Sin acceso
          </li>
        )}
      </NavSection>

      <NavSection label="Global">
        {tools.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            active={isActive(pathname, item.path)}
          />
        ))}
      </NavSection>

      {isAdmin && (
        <NavSection label="Admin">
          {admin.map((item) => (
            <NavLink
              key={item.path}
              item={item}
              active={isActive(pathname, item.path)}
            />
          ))}
        </NavSection>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function NavSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="section-label px-4 pt-2 pb-1.5 hidden lg:block">{label}</div>
      <ul className="flex flex-col gap-0.5 px-2">{children}</ul>
    </div>
  );
}

function NavLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  const hasCount = item.count !== undefined && item.count > 0;

  if (item.disabled) {
    return (
      <li>
        <div
          aria-disabled="true"
          title="Sección no activa todavía"
          className={cn(
            "relative flex items-center gap-3 rounded-lg px-3 py-2 h-9",
            "text-sm text-text-disabled opacity-60 cursor-not-allowed",
          )}
        >
          <Icon size={16} strokeWidth={1.5} className="shrink-0" />
          <span className="hidden lg:block flex-1 truncate">{item.label}</span>
          <span className="hidden lg:inline-block font-mono text-2xs uppercase tracking-wider text-text-disabled">
            soon
          </span>
        </div>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={item.path}
        className={cn(
          "relative flex items-center gap-3 rounded-lg px-3 py-2 h-9",
          "text-sm text-text-secondary",
          "hover:bg-surface-2 hover:text-text-primary transition-colors",
          active && "bg-surface-3 text-text-primary",
        )}
        aria-current={active ? "page" : undefined}
        title={item.label}
      >
        {active && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-1 h-1 rounded-full bg-magenta"
          />
        )}
        <span className="relative shrink-0">
          <Icon size={16} strokeWidth={1.5} />
          {/* Count dot — solo visible cuando el sidebar está colapsado (debajo de lg). */}
          {hasCount && (
            <span
              aria-hidden="true"
              className="lg:hidden absolute -top-1 -right-1 w-2 h-2 rounded-full bg-magenta ring-2 ring-surface-1"
            />
          )}
        </span>
        <span className="hidden lg:block flex-1 truncate">{item.label}</span>
        {hasCount && (
          <span
            className={cn(
              "hidden lg:inline-block font-mono text-xs tabular-nums",
              active ? "text-text-primary" : "text-text-muted",
            )}
          >
            {item.count}
          </span>
        )}
      </Link>
    </li>
  );
}

function ClientLink({
  client,
  pathname,
}: {
  client: ClientCard;
  pathname: string;
}) {
  const active = pathname.startsWith(`/c/${client.slug}`);
  const statusVariant: StatusDotVariant =
    client.health === "ok"
      ? "success"
      : client.health === "warn"
        ? "warning"
        : "muted";
  return (
    <li>
      <Link
        to="/c/$clientSlug"
        params={{ clientSlug: client.slug }}
        className={cn(
          "relative flex items-center gap-3 rounded-lg px-3 py-2 h-9",
          "text-sm text-text-secondary",
          "hover:bg-surface-2 hover:text-text-primary transition-colors",
          active && "bg-surface-3 text-text-primary",
        )}
        aria-current={active ? "page" : undefined}
        title={client.label}
      >
        {active && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-1 h-1 rounded-full bg-magenta"
          />
        )}
        <StatusDot variant={statusVariant} size={8} className="shrink-0" />
        <span className="hidden lg:block flex-1 truncate">{client.label}</span>
      </Link>
    </li>
  );
}

function UserMenu({ name, role }: { name: string; role: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const initials = getInitials(name);

  return (
    <div
      ref={ref}
      className="relative border-t border-border-subtle p-3"
    >
      {open && (
        <div
          role="menu"
          className={cn(
            "absolute left-3 right-3 bottom-[calc(100%-8px)]",
            "card-surface p-1.5 z-20",
          )}
        >
          <button
            type="button"
            role="menuitem"
            className="flex items-center w-full gap-2.5 rounded-lg px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
            onClick={() => setOpen(false)}
          >
            <Settings size={16} strokeWidth={1.5} />
            <span className="flex-1 text-left">Settings</span>
          </button>
          <ThemeToggle variant="menu-item" />
          <div className="my-1 h-px bg-border-subtle" />
          <button
            type="button"
            role="menuitem"
            className="flex items-center w-full gap-2.5 rounded-lg px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
            onClick={() => {
              setOpen(false);
              window.location.assign("/login");
            }}
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span className="flex-1 text-left">Logout</span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex items-center w-full gap-2.5 rounded-lg px-2 py-1.5",
          "hover:bg-surface-2 transition-colors",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className={cn(
            "flex items-center justify-center w-8 h-8 rounded-full shrink-0",
            "bg-surface-3 border border-border-default",
            "font-sans text-xs font-medium text-text-primary",
          )}
        >
          {initials}
        </span>
        <span className="hidden lg:flex flex-col min-w-0 flex-1 text-left">
          <span className="text-sm text-text-primary truncate">{name}</span>
          <span className="text-xs text-text-muted truncate capitalize">{role}</span>
        </span>
        <ChevronUp
          size={14}
          strokeWidth={1.5}
          className={cn(
            "hidden lg:block shrink-0 text-text-muted transition-transform",
            !open && "rotate-180",
          )}
        />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function isActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}
